<template>
  <div :class="styles['auth-layout']">
    <div :class="styles['auth-layout__banner-wrapper']">
      <div :class="styles['auth-layout__banner']">
        <VAiLogo :class="styles['auth-layout__logo']" />
        <h3 :class="styles['auth-layout__banner-title']">
          Move Faster. Collaborate Better.
        </h3>
        <p :class="styles['auth-layout__banner-description']">
          Your business powered by AI.
        </p>
        <LottieAnimation
          :class="styles['auth-layout__animation']"
          :animation-data="LoginAnimation"
          loop
        />
      </div>
      <div :class="styles['auth-layout__login-bg-blur']" />
      <LoginBg :class="styles['auth-layout__login-bg']" />
    </div>
    <div :class="styles['auth-layout__content-wrapper']">
      <div
        :class="[
          styles['auth-layout__logo-wrapper'],
          {
            [styles['auth-layout__logo-wrapper--top']]: isLogoTop
          }
        ]"
      >
        <CompanyLogo
          v-if="hasCompany"
        />
        <VSimpleLogo
          v-else
        />
      </div>
      <router-view />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide, useCssModule } from "vue"
import CompanyLogo from '@/components/features/CompanyLogo.vue'
import VSimpleLogo from '@/assets/images/vsimple-auth-logo.svg'
import LoginBg from '@/assets/images/LoginBg.svg'
import { useRoute } from "vue-router"
import { LottieAnimation } from 'lottie-web-vue'
import LoginAnimation from '@/assets/lottie/LoginAnimation.json'
import VAiLogo from '@/assets/icons/common/VAiLogo.svg'

const route = useRoute()

const hasCompany = computed(() => {
  const { company } = route.params

  return company && !['undefined', 'null'].includes(company as string)
})

const isLogoTop = computed(() => {
  return [
    'create-account',
    'company-create-account',
  ].includes(route.name as string)
})

provide('hasCompany', hasCompany.value)

const styles = useCssModule()
</script>

<style lang="scss" module>
.auth-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 100%;
  background-color: var(--v-app-bg);

  @include media-breakpoint-down(md) {
    grid-template-columns: 1fr;
  }
}

.auth-layout__banner-wrapper {
  position: relative;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
  @include media-breakpoint-down(md) {
    display: none;
  }
}

.auth-layout__content-wrapper {
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  filter: var(--v-primary-shadow);
  @include media-breakpoint-down(sm) {
    padding: 10px;
  }
}
.auth-layout__logo-wrapper {
  position: absolute;
  max-width: 250px;
  width: 100%;
  height: auto;
  left: 50%;
  top: 40px;
  transform: translateX(-50%);

  svg {
    width: 100%;
    height: auto;
  }

  @include media-breakpoint-down(sm) {
    top: 20px;
    max-width: 200px;
  }
}
.auth-layout__logo-wrapper--top {
  top: 20px;
}

.auth-layout__banner-title {
  font-size: 36px;
  font-family: var(--v-font-primary);
  text-align: center;
  margin-bottom: 16px;
  color: #191347;
  font-weight: 600;
}

.auth-layout__banner-description {
  font-size: 16px;
  font-family: var(--v-font-primary);
  color: #191347;
  margin: 0 auto 73px;
  max-width: 400px;
  text-align: center;
}

.auth-layout__banner {
  z-index: 1;
  display: flex;
  flex-direction: column;
}

.auth-layout__login-bg-blur {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: url('@/assets/images/bg-filter.png');
  opacity: .18;
}

.auth-layout__login-bg {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
}

.auth-layout__animation {
  max-width: 464px;
  margin: 0 auto;
}

.auth-layout__logo {
  height: 56px;
  width: 89px;
  margin: 0 auto 32px;
}
</style>
